'use client'
import {useCallback, useEffect} from "react";
import {when} from "mobx";
import {GOOGLE_ID} from "./config";
import {useAuth} from "./hooks/useAuth";
import {
    isReportRoute,
    isWidgetRoute,
    paramsGet,
    paramsRemove,
    suppressNetworkError,
    useUser,
    validateEmail
} from "./utils";
import {HANDOFF_PARAMS, readHandoffLaunch, readHumanize, readHumanizerMode, readScanModes} from "./scanParams";
import {logEvent} from "./analytics";
import {fetchRequestsInfo, getHistoryAnalysis, getSharedText, getSharedFiles, isUnreachable, mapAnonUser} from "./requests";
import {useStores} from "./stores/rootStore";
import {observer} from "mobx-react-lite";
import "stores/actions"
import {useLanguage} from "i18n/LanguageProvider";
import {useLocalizedNavigate, withLanguagePrefix} from "i18n/hooks";
import {getStoredToken, persistAuthData, restoreInputDraft} from "./persistence";
import {useNavigation} from "react-router-dom";
import {gtmAppOpen, gtmEvent, gtmUtmArrival, LANDING_GTM_BRIDGE_PARAMS} from "./utils/gtm";
import {captureUtm, DELIVERY_KEY, markUtmReported, pendingUtm, UTM_KEYS} from "./utils/utm";
import {acceptHumanizerHandoff, acceptHumanizerFilesHandoff} from './humanizerHandoff';
import {pendingHumanizerEntry} from './humanizerEntry';
import {initCello} from "./cello";
import {tagClarity} from "./clarity";

const ANON_ROUTES = ['scan', 'subscription', 'login', 'history'];

// The scan modes the page was opened with, snapshotted at module load — before anything can
// write to the query. Everything below runs after auth, by which time the scan page mirrors
// the live selection into the URL, so the live query can no longer answer "did this link ask
// for a mode?". Null when the URL named none.
const INCOMING_SCAN_MODES = readScanModes(window.location.search);
// Read on arrival, like the modes: the scan page rewrites the query once it mounts.
const INCOMING_HUMANIZE = readHumanize(window.location.search);
const INCOMING_HUMANIZER_MODE = readHumanizerMode(window.location.search);
const INCOMING_HUMANIZER_LANDING = !!pendingHumanizerEntry();

// Same snapshot-at-load reasoning for the hand-off's asks. Its sign-up or checkout is
// launched once: init re-runs on every login/logout, and the arrival must not open again.
let handoffLaunch = readHandoffLaunch(window.location.search);
const INCOMING_AUTORUN = handoffLaunch.autorun;

const isAnonRoute = () => {
    const pathname = window.location.pathname;
    return ANON_ROUTES.some(route => pathname.includes(route));
}

// The arrival's attribution params (?from=, UTM, the GTM bridge). Once reported they read as
// absent, and leave the URL together when init ends — one URL change, one new Clarity page.
const consumed = new Set()
const readParam = (key) => consumed.has(key) ? null : paramsGet(key)
const consumeParams = (...keys) => keys.forEach(key => consumed.add(key))
const removeConsumedParams = () => {
    paramsRemove(...consumed)
    consumed.clear()
}

const adoptExternalUUID = () => {
    const from = paramsGet("from")
    if (!from) return
    if (getStoredToken()) return
    localStorage.setItem('uuid', from)
}

// Snapshotted at module load, like the scan modes above: reporting them needs a
// session, and a link from an email lands on whatever route the letter pointed
// at — only a few of them (ANON_ROUTES) authenticate an anonymous visitor at all.
captureUtm(window.location.search);

// Joins landing and app recordings when a returning visitor keeps the app's own uuid.
tagClarity("landing_uuid", paramsGet("from"));

const reportUTM = async () => {
    const utm = pendingUtm()
    if (!utm) return
    console.log("Report utm")
    await logEvent(gtmUtmArrival({...utm, page_type: 'app_open', entrypoint: 'first_load'}))
    markUtmReported()
    // Only now: until this point the query is what GTM and GA4 read the visit's
    // own source off, and the tags have to outlive the app's first page_view.
    consumeParams(...UTM_KEYS, DELIVERY_KEY)
}

let consentScheduled = false;
const updateConsent = () => {
    // TODO make like in landing
    // https://medium.com/@sdanvudi/configuring-google-cookies-consent-with-next-js-15-ca159a2bea13
    console.log("Init consent effect")
    const setConsent = async () => {
        const consentMode = {
            'functionality_storage': 'granted',
            'security_storage': 'granted',
            'ad_storage': 'granted',
            'analytics_storage': 'granted',
            'personalization_storage': 'granted',
            'ad_user_data': 'granted',
            'ad_personalization': 'granted',
        };
        const ReactGA = (await import("react-ga4")).default;
        ReactGA.gtag('consent', 'update', consentMode);
        localStorage.setItem("consent", "true");
        logEvent(gtmEvent("GTM_consent_update", {page_type: "app_open", entrypoint: "auto_consent"}))
    }

    // Both the anonymous and the authenticated init call this before the delayed
    // setConsent persists the flag, so the flag alone lets it fire twice.
    if (consentScheduled || localStorage.getItem("consent")) return;
    consentScheduled = true;
    setTimeout(setConsent, 2000)
}

const setAnalyticsUserId = async (username) => {
    // SET USER LOGGED
    if (!username) return;
    console.log("Set ga4 user_id effect")
    // https://developers.google.com/analytics/devguides/collection/ga4/user-id?client_type=gtag
    const ReactGA = (await import("react-ga4")).default;
    ReactGA.gtag("config", GOOGLE_ID, {user_id: username});
    console.log("Set ga4 user_id", username)
}

const setContentSquareID = (uuid) => {
    // Contentsquare identify - https://docs.contentsquare.com/en/web/track-users/
    if (!uuid) return;
    window._uxa = window._uxa || [];
    window._uxa.push(["identify", uuid]);
    console.log("Initialize contentsquare user uuid", uuid)
}

const firstEvent = async () => {
    const KEY = "IA__INIT";
    const now = Date.now();
    const last = parseInt(sessionStorage.getItem(KEY), 10);
    if (last && now - last < 60_000) return;
    sessionStorage.setItem(KEY, String(now));
    await logEvent(gtmAppOpen({page_location: window.location.href, page_referrer: document.referrer, page_title: document.title, page_type: 'app_open', entrypoint: 'first_load'}));
}

// Привязка анонимного владельца файлов (uuid лендинга, параметр `from`) к
// залогиненному аккаунту. Вызывается и до инъекции landing-файлов, и в общем
// init-потоке — кэшируем промис, чтобы map_anon_user ушёл ровно один раз.
// Привязываем ВСЕГДА при наличии `from`: совпадение `from` с localStorage uuid
// НЕ значит, что анон уже привязан к текущему аккаунту на бэке
// (anon.registered_user мог быть NULL) — иначе скан файла падает с
// file_with_uuid_not_found. Бэк (connect_anon_with_registered) идемпотентен.
let anonMapPromise = null
const ensureAnonMapped = (isAnon) => {
    if (anonMapPromise) return anonMapPromise
    // Skip while still anonymous: `from` equals this session's own anon uuid, so
    // claiming would be a self-claim (no-op on the backend, and historically it
    // poisoned registered_user). The anon already owns its files; the claim only
    // matters once a real account is logged in.
    if (isAnon) return Promise.resolve()
    const from = readParam("from")
    if (!from) return Promise.resolve()
    anonMapPromise = suppressNetworkError(mapAnonUser(from))
    return anonMapPromise
}

// The landing's cross-origin GTM context (see LANDING_GTM_BRIDGE_PARAMS), spent once the
// arrival has been reported. Left in place it outlives the arrival it describes: the scan
// page's URL is what the user reloads, bookmarks and shares, and every one of those would
// carry a stale "came from the landing hero" attribution.
const clearLandingGtmBridge = () => {
    consumeParams(...LANDING_GTM_BRIDGE_PARAMS)
}

const saveUUID = async (isAnon) => {
    const from = readParam("from")
    if (from) {
        await ensureAnonMapped(isAnon)
        await logEvent(gtmEvent("GTM_uuid_provided", {uuid: from, page_type: "app_open", entrypoint: "first_load"}))
        consumeParams("from")
    }
}

const getParamsText = async () => {
    // New flow: landing POSTs the text and hands off a one-time token. Remove
    // the param before the network call so a double mount can't fetch twice
    // (the token is consumed server-side on first read).
    const token = paramsGet("text_token")
    if (token) {
        paramsRemove("text_token")
        try {
            return await getSharedText(token)
        } catch (e) {
            console.error("Failed to load shared text", e)
            return null
        }
    }
    // Legacy flow: short text passed directly in the URL.
    const text = paramsGet("text")
    if (text) {
        paramsRemove("text")
        return text
    }
    return null
}

const getParamsFiles = async () => {
    // The landing uploads files and hands off a one-time token bundling their
    // uuids. Remove the param before the network call so a double mount can't
    // fetch twice (the token is consumed server-side on first read).
    const token = paramsGet("files_token")
    if (!token) return null
    paramsRemove("files_token")
    try {
        return await getSharedFiles(token)
    } catch (e) {
        console.error("Failed to load shared files", e)
        return null
    }
}

// A hand-off carries a Scan the user already pressed on the landing, so the app presses Run scan
// for them — through the same handle the button uses (inputStore.makeRunScanRequest), which
// starts the run or opens the gate that explains why it can't: the paywall, or the signup form on
// an anon's first wall. Nothing here decides whether the run is possible; text and files differ
// only in what has to settle before the press.
const prepareHandoffRun = async (input) => {
    // The modes named by the link are applied here rather than left to the scan page: it sits
    // behind React.lazy and can mount after this, and then `?plagiarism=true&files_token=…`
    // would run — and be charged — as the default deep scan.
    if (INCOMING_SCAN_MODES) input.applyScanModes(INCOMING_SCAN_MODES);
    // Judge the limits on the LIVE account. isReady() — which is what lets init run at all — is
    // satisfied by the localStorage snapshot the moment the token is applied (UserStore seeds
    // `user` from getCachedUser), so we can get here while /users/me is still in flight, and a
    // snapshot taken when the account was out of scans would open a paywall over a quota that
    // has since reset. A user pressing Run scan is past this by seconds; the hand-off is not.
    // A hand-off that leaves the press to the user has no limits to judge yet.
    if (INCOMING_AUTORUN) await input.root.user.refresh();
}

// Contract with the extension's app-bridge content script (It-s-AI/extension, entrypoints/app-bridge.content.ts).
const EXTENSION_GUEST_REQUEST = 'itsai:extension-guest-request'
const EXTENSION_GUEST_REPLY = 'itsai:extension-guest'
const EXTENSION_REPLY_TIMEOUT_MS = 2000

/** The extension's guest session token, or null when no extension answers (or it holds an account, not a guest). */
const requestExtensionGuest = () => new Promise((resolve) => {
    const origin = window.location.origin
    const onMessage = (e) => {
        if (e.source !== window || e.origin !== origin || e.data?.type !== EXTENSION_GUEST_REPLY) return
        finish(typeof e.data.token === 'string' ? e.data.token : null)
    }
    const timer = setTimeout(() => finish(null), EXTENSION_REPLY_TIMEOUT_MS)
    const finish = (token) => {
        clearTimeout(timer)
        window.removeEventListener('message', onMessage)
        resolve(token)
    }
    window.addEventListener('message', onMessage)
    window.postMessage({type: EXTENSION_GUEST_REQUEST}, origin)
})

// ?scan=<analysis uuid> — "Open in app" from the browser extension: the scan shown the way a history Restore
// shows it. History only serves this session's own scans. A scan the extension made as a guest (&guest=1) is
// opened by taking over that guest session, when this browser is a guest too — the visitor then carries on in
// the app as the same guest. Anything else (e.g. the browser holds an account) opens as the public report.
const openLinkedScan = async (input, navigate, userStore) => {
    const uuid = paramsGet("scan")
    if (!uuid) return false
    const fromGuest = paramsGet("guest") === "1"
    paramsRemove("scan", "guest")
    let scan = await getHistoryAnalysis(uuid).catch(() => null)
    if (!scan && fromGuest && userStore.isAnonymous) {
        const token = await requestExtensionGuest()
        // auth() swaps the token in before validating it: persist only one that worked,
        // so a dead extension session never overwrites this browser's own guest.
        if (token && await userStore.auth(token)) {
            persistAuthData(userStore)
            scan = await getHistoryAnalysis(uuid).catch(() => null)
        }
    }
    if (!scan) {
        navigate(`/report/${uuid}`, {replace: true})
        return true
    }
    input.openAnalysis({uuid, date: scan.date, text: scan.text, items: scan.items})
    input.root.layout.setWorkspace('scan')
    return true
}

const restoreInputState = async (input, batchFile, isAnon, navigate, userStore) => {
    // A report page renders a stored analysis. Restoring scan-page state over it would
    // overwrite the report's text and — via checkFlags, which falls back to Deep scan when
    // nothing is selected — its scan mode, racing whatever the report response applied.
    if (isReportRoute()) return;

    if (await openLinkedScan(input, navigate, userStore)) return;

    // The widget brings its draft back only on its host's word (ScanWidget), never on a load.
    const draft = isWidgetRoute() ? null : restoreInputDraft();

    // The modes the user left behind, from before a login redirect. A link that names its
    // modes — a landing hand-off (?grammar=true) or a shared URL — is the fresher intent and
    // wins; the scan page applies those itself (useScanModesInUrl). Judged against the URL
    // the page ARRIVED with, not the current one: this runs after auth, by which time the
    // scan page has already mirrored the live selection back into the query.
    if (draft?.modes && !INCOMING_SCAN_MODES) {
        input.applyScanModes(draft.modes)
    }

    // Files handed off from the landing: drop them into the upload widget as a
    // finished upload and run the scan, exactly as the text flow below does — the
    // Scan press on the landing is the user's intent for files just as much.
    const paramsFiles = await getParamsFiles();
    if (paramsFiles && paramsFiles.length > 0) {
        // Файлы пришли с лендинга под анон-uuid: гарантируем привязку анона к
        // аккаунту ДО того как файлы станут сканируемыми, иначе первый Run scan
        // уйдёт раньше map_anon_user и упадёт с file_with_uuid_not_found.
        await ensureAnonMapped(isAnon);
        if (INCOMING_HUMANIZE) {
            await acceptHumanizerFilesHandoff(input.root, paramsFiles, INCOMING_HUMANIZER_MODE,
                INCOMING_HUMANIZER_LANDING && INCOMING_AUTORUN);
            return;
        }
        batchFile.injectUploadedFiles(paramsFiles);
        await prepareHandoffRun(input);
        // Files only: a document still under OCR (status=processing) has no extracted text yet,
        // which is the same reason Run scan stays disabled for it (hasRunnableInput →
        // isFilesLoading). `when` fires immediately when nothing is pending.
        if (INCOMING_AUTORUN) when(() => !batchFile.isFilesLoading(), () => input.makeRunScanRequest('landing_autoscan'));
        return;
    }

    // Resolved last: the token path is async, but flags/draft must apply
    // immediately regardless of network latency.
    const paramsText = await getParamsText();

    if (paramsText && INCOMING_HUMANIZE) {
        await acceptHumanizerHandoff(input.root, paramsText, INCOMING_HUMANIZER_MODE,
            INCOMING_HUMANIZER_LANDING && INCOMING_AUTORUN);
    } else if (paramsText) {
        input.setText(paramsText);
        input.activeEditor.setText(paramsText);
        // Unlike files, a text is ready to scan at once — nothing to wait for. The length gate
        // (preScanChecks caps an anon at words_per_generation_limit) is no reason to stay
        // silent: this answers the Scan pressed on the landing, exactly as for files above.
        await prepareHandoffRun(input);
        if (INCOMING_AUTORUN) input.makeRunScanRequest('landing_autoscan');
    } else if (draft?.text) {
        void input.restoreDraft(draft);
        // Draft restored after a login that began at the limit auth form (OAuth
        // reload lands here): continue the user's scan. A voluntary login persists
        // the draft with autoRun=false, so we only restore the text, never scan.
        if (draft.autoRun) input.makeRunScanRequest();
    }
}

const checkForOnboardingLaunch = (onboarding) => {
    if (isWidgetRoute()) return;
    if (paramsGet('onboarding') === "true") {
        onboarding.startOnboarding(true);
        paramsRemove("onboarding");
    }
}

// ?signup= opens the auth modal on arrival — the CTA target for external "Sign up now"
// links (e.g. Cello's referral welcome banner) and the widget's limit form. promptLogin is
// anon-gated, so a logged-in visitor just has the param consumed. ?upgrade= opens a plan's
// checkout; an anon signs in first and gets it from LoginCompleteHandler.
const checkForHandoffLaunch = async (input) => {
    if (isWidgetRoute()) return;
    paramsRemove(...HANDOFF_PARAMS);
    const {signup, upgrade} = handoffLaunch;
    handoffLaunch = {...handoffLaunch, signup: null, upgrade: null};
    if (upgrade) {
        if (input.root.user.isAnonymous) {
            input.promptLogin('general', {action: {type: 'upgrade', plan: upgrade}});
            return;
        }
        // The plan is judged on the live account, not the cached one init starts from: a plan
        // bought since would otherwise be sold again.
        // A failed refresh still opens it, judged on the cached account.
        await input.root.user.refresh().catch(() => {});
        input.openPlanCheckout(upgrade);
    } else if (signup) {
        input.promptLogin(signup);
    }
}

const initAnalytics = async (uuid) => {
    updateConsent()
    // setContentSquareID(uuid)
    await setAnalyticsUserId(uuid)
}

const authByParamsToken = async (auth) => {
    const token = paramsGet("token")
    if (token) {
        paramsRemove("token")
        return await auth(token)
    }
    return false
}

const authByStorageToken = async (auth) => {
    const token = getStoredToken()
    if (token) {
        return await auth(token)
    }
    return false
}

const removeOmniMonkProvidedBy = () => {
    const poweredBy = document.getElementsByClassName("powered-by")[0]
    if (poweredBy) {
        poweredBy.remove()
    }
}

const handlePopupShow = (event, user) => {
    removeOmniMonkProvidedBy()
    console.log('optimonk event: ', event, event.parameters);
    setTimeout(() => {
        if (validateEmail(user.username)) {
            const email = Array.from(document.getElementsByClassName("om-inputs"))
                .filter(item => item.type === "email")[0]
            if (email) {
                email.value = user.username
                console.log("Set popup email", email)
            }
        }
        const name = Array.from(document.getElementsByClassName("om-inputs"))
            .filter(item => item.placeholder.toLowerCase().includes("name"))[0]
        if (name) {
            console.log("Set popup name", name)
            name.value = user.name
        }
    }, 100)
}

const readDebugParams = () => {
    const debug = paramsGet("debug")
    if (debug) {
        const obj = JSON.parse(debug)
        if (obj.enabled) {
            localStorage.setItem("ENABLED", obj.enabled)
        }
        if (obj.disabled) {
            localStorage.setItem("DISABLED", obj.disabled)
        }
        localStorage.setItem("debug", debug)
        paramsRemove("debug")

        window.location.reload()
    }
}

const anonAuth = async (auth, userStore) => {
    if (!isAnonRoute()) return false
    try {
        const info = await fetchRequestsInfo();
        await auth(info.token)
        return true
    } catch (e) {
        console.error("Anonymous auth failed", e)
        // No guest session: refused (anon_limit_exceeded) → the login is the way out;
        // unreachable even after the retries → the reload (BootFailedModal).
        if (isUnreachable(e)) userStore.setBootFailure('unreachable')
        else userStore.setAnonAuthBlocked(true)
        return false
    }
}

export const InitAppComponent = observer(({children}) => {
    const {authenticated, login, ready} = useAuth()
    const {user: userStore, input, onboarding, batchFile} = useStores()
    const root = useStores();
    const user = useUser()
    const userReady = userStore.isReady()
    const navigate = useLocalizedNavigate()
    const navigation = useNavigation()
    const {lang} = useLanguage()

    const auth = async () => {
        if (await authByParamsToken(userStore.auth)) { persistAuthData(userStore); return; }
        if (await authByStorageToken(userStore.auth)) { persistAuthData(userStore); return; }
        if (await anonAuth(userStore.auth, userStore)) { persistAuthData(userStore); return; }
    }

    const init = useCallback(async () => {
        const isAnon = userStore.isAnonymous
        await restoreInputState(input, batchFile, isAnon, navigate, userStore)
        checkForOnboardingLaunch(onboarding)
        void checkForHandoffLaunch(input)
        // Referral: capture/report the ucc and boot the widget. Fire-and-forget —
        // nothing below depends on it, and the widget iframes must not load it.
        if (!isWidgetRoute()) {
            void initCello(userStore.user)
        }
        await initAnalytics(userStore.user.uuid)
        await Promise.all([
            suppressNetworkError(firstEvent()),
            suppressNetworkError(saveUUID(isAnon)),
            suppressNetworkError(reportUTM()),
        ])
        // Last: everything that reports the URL the app was opened with has now reported it.
        clearLandingGtmBridge()
    }, [userStore.user])

    // WHEN NOT AUTHENTICATED (on site open)
    useEffect(() => {
        if (authenticated) return;
        if (!ready) return;
        if (root.error !== null) return;
        console.log("Not authenticated effect")
        adoptExternalUUID()
        updateConsent()
        auth()
    }, [authenticated, ready]);

    // WHEN AUTHENTICATED (when have token and user data)
    useEffect(() => {
        if (!authenticated) return;
        if (!userReady) return;
        console.log("Authenticated effect")

        // Whatever was reported leaves the URL, even if init stopped halfway.
        init().finally(removeConsumedParams)
    }, [authenticated, userReady, user?.is_anon])

    // Policy acceptance is now handled during modal onboarding

    // Handle OmniMonk popup provider
    useEffect(() => {
        const body = document.getElementsByTagName("body")[0];

        const observer = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'childList') {
                    removeOmniMonkProvidedBy()
                }
            }
        });

        observer.observe(body, {childList: true, subtree: false});
        // var omEvents = window.omEvents || [];

        // Check popup is open
        // document.getElementsByClassName("om-popup-container")[0].style.display === "block"
        // ON POPUP SHOW
        document.getElementsByTagName('html')[0].addEventListener(
            'optimonk#campaign-show', function (event) {
                handlePopupShow(event, user)
            });

        document.cookie = "popup=" + (user?.popup || "") + "; Max-Age=3600"
        removeOmniMonkProvidedBy()
    }, [userReady]);

    useEffect(() => {
        readDebugParams()
    }, []);

    // The flags→URL half of the sync lives with the scan page (useScanModesInUrl), which is
    // the only route the params belong to and which re-mounts when the user comes back to it.

    return <>
        {children}
    </>
})
